/* KIYO: actions for "Deine Konfiguration" groups in cart + drawer.
 *
 * Markup contract (snippets/cart-products.liquid):
 *   header row: .kiyo-config-group__header
 *     - button[data-kiyo-remove="<id>"]                  remove whole group
 *     - button[data-kiyo-share] + data-copied-label      share / copy link
 *     - button[data-kiyo-save]  + data-form-id, data-*     save via Klaviyo form
 *     - [data-kiyo-units="<id>"] with units buttons      quantity stepper
 *   carrier rows: tr[data-kiyo-config-line="<id>"] — hidden, carry the
 *     theme's data-key (line item key) and data-kiyo-base (qty at units=1)
 *
 * The parts list is a native <details>; no JS involved. All controls are
 * hidden until this module reveals them (progressive enhancement: without
 * JS the head shows plain text and no dead buttons).
 */
(() => {
  const reveal = () => {
    document
      .querySelectorAll('[data-kiyo-remove], [data-kiyo-share], [data-kiyo-save], .kiyo-config-group__units-btn')
      .forEach((el) => { el.hidden = false; });
  };

  const groupRows = (id) =>
    [...document.querySelectorAll(`[data-kiyo-config-line="${CSS.escape(id)}"]`)];

  const postUpdates = async (updates) => {
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
    });
  };

  const markBusy = (id) => {
    document.querySelectorAll(`[data-kiyo-config-id="${CSS.escape(id)}"]`)
      .forEach((row) => row.classList.add('kiyo-config-group--removing'));
  };

  // Remove ALL lines of a configuration in one go, then reload — the
  // server-rendered cart keeps page, drawer and bubble in sync.
  const removeGroup = async (id) => {
    const updates = {};
    groupRows(id).forEach((row) => {
      const key = row.getAttribute('data-key');
      if (key) updates[key] = 0;
    });
    if (!Object.keys(updates).length) return;
    markBusy(id);
    try {
      await postUpdates(updates);
    } catch (error) {
      console.warn('[Kiyo] Konfiguration entfernen fehlgeschlagen:', error);
    } finally {
      window.location.reload();
    }
  };

  // Scale ALL lines of a configuration to base x units.
  const setUnits = async (id, units) => {
    const updates = {};
    groupRows(id).forEach((row) => {
      const key = row.getAttribute('data-key');
      const base = parseInt(row.getAttribute('data-kiyo-base'), 10) || 1;
      if (key) updates[key] = base * units;
    });
    if (!Object.keys(updates).length) return;
    markBusy(id);
    try {
      await postUpdates(updates);
    } catch (error) {
      console.warn('[Kiyo] Anzahl aendern fehlgeschlagen:', error);
    } finally {
      window.location.reload();
    }
  };

  const shareConfig = async (button) => {
    const url = button.getAttribute('data-kiyo-share');
    if (!url) return;
    // Title makes the OS share sheet show "Meine Kiyo Konfiguration" instead
    // of the raw ?setup= URL (localized via data attribute from the snippet).
    const title = button.getAttribute('data-kiyo-share-title') || '';
    try {
      if (navigator.share) {
        await navigator.share(title ? { title, text: title, url } : { url });
        return;
      }
      throw new Error('no web share');
    } catch (error) {
      if (error && error.name === 'AbortError') return; // user closed the sheet
      try {
        await navigator.clipboard.writeText(url);
        const label = button.querySelector('span:last-child');
        const copied = button.getAttribute('data-copied-label');
        if (label && copied) {
          const original = label.textContent;
          label.textContent = copied;
          setTimeout(() => { label.textContent = original; }, 2000);
        }
      } catch (clipboardError) {
        console.warn('[Kiyo] Link teilen fehlgeschlagen:', clipboardError);
      }
    }
  };

  // ── Save: same Klaviyo path as the configurator ───────────────────────────
  // The configurator's "Speichern" opens a Klaviyo onsite form, then fires the
  // event "Configurator Saved". Doing exactly that here means the three live
  // Configurator flows (DE/EN/FR) work unchanged from the cart. Everything the
  // mail template renders (imageUrl, configUrl, addToCartUrl, breakdownText,
  // dimensionsText, shippingDuration, totalPrice) already sits on the line as
  // data attributes — verified against template RX9G8a on 2026-08-25.
  //
  // $event_id carries a ":cart" suffix on purpose: Klaviyo dedupes by that id,
  // so reusing the bare config id would silently swallow a cart save from
  // someone who had already saved the same build in the configurator — the
  // customer would click "Speichern" and never get a mail.

  const EMAIL_RE = /.+@.+\..+/;
  const KLAVIYO_COMPANY_ID = 'XRK4GJ';

  // Klaviyo's metaData keys depend on the form's field config, so try the known
  // variants and fall back to a key-name scan (same approach as the configurator).
  const extractEmail = (md) => {
    const m = (md && typeof md === 'object') ? md : {};
    const nested = [m.$consent, m.identifiers, m.profile].map((v) => (v && typeof v === 'object' ? v : {}));
    const candidates = [m.email, m.$email, m.EMAIL, m.Email].concat(nested.map((n) => n.email));
    for (const c of candidates) {
      if (typeof c === 'string' && EMAIL_RE.test(c.trim())) return c.trim();
    }
    for (const k of Object.keys(m)) {
      const v = m[k];
      if (k.toLowerCase().includes('email') && typeof v === 'string' && EMAIL_RE.test(v.trim())) return v.trim();
    }
    return '';
  };

  const extractFirstName = (md) => {
    const m = (md && typeof md === 'object') ? md : {};
    const v = m.$first_name ?? m.first_name ?? m.FirstName ?? m.firstName;
    return typeof v === 'string' ? v.trim() : '';
  };

  let pendingSave = null;

  const feedback = (button, label) => {
    const span = button.querySelector('span:last-child');
    if (!span || !label) return;
    const original = span.textContent;
    span.textContent = label;
    setTimeout(() => { span.textContent = original; }, 2500);
  };

  const trackSaved = (button, email, firstName) => {
    const d = button.dataset;
    const total = parseFloat(d.total);
    const props = {
      $event_id: `${d.kiyoSave}:cart`,
      source: 'cart',
      region: d.region || '',
      formEmail: email,
      breakdownText: d.parts || '',
      dimensionsText: d.dimensions || '',
      shippingDuration: d.shipping || '',
      currency: d.currency || '',
    };
    // Only send what is actually there — the mail template has its own
    // fallbacks and an empty string would suppress them.
    if (d.configUrl) props.configUrl = d.configUrl;
    if (d.imageUrl) props.imageUrl = d.imageUrl;
    if (d.cartUrl) props.addToCartUrl = d.cartUrl;
    if (Number.isFinite(total) && total > 0) props.totalPrice = total;

    // Straight to Klaviyo's client events API: klaviyo.push(['track']) right
    // after a form submit got dropped silently by Klaviyo's onsite JS
    // (2026-10-15, same fix as the configurator). unique_id = $event_id
    // dedupes repeated sends; keepalive survives the page reload.
    if (email) {
      fetch(`https://a.klaviyo.com/client/events/?company_id=${KLAVIYO_COMPANY_ID}`, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json', revision: '2025-01-15' },
        body: JSON.stringify({ data: { type: 'event', attributes: {
          properties: props,
          metric: { data: { type: 'metric', attributes: { name: 'Configurator Saved' } } },
          profile: { data: { type: 'profile', attributes: {
            email: email.toLowerCase(),
            ...(firstName ? { first_name: firstName } : {}),
          } } },
          unique_id: props.$event_id,
        } } }),
      })
        .then((r) => { if (!r.ok) console.warn('[Kiyo] Configurator Saved (Warenkorb) abgelehnt:', r.status); })
        .catch((error) => console.warn('[Kiyo] Klaviyo-Event fehlgeschlagen:', error));
      console.info('[Kiyo] Configurator Saved (Warenkorb) gesendet:', props.$event_id);
      return;
    }

    // No email in the form payload: only the onsite identity can carry the event.
    const push = () => {
      const kl = window.klaviyo;
      if (!kl || typeof kl.push !== 'function') return false;
      try {
        kl.push(['track', 'Configurator Saved', props]);
        console.info('[Kiyo] Configurator Saved (Warenkorb, ohne E-Mail) gesendet:', props.$event_id);
      } catch (error) {
        console.warn('[Kiyo] Klaviyo-Event fehlgeschlagen:', error);
      }
      return true; // an attempt happened — never retry on throw
    };
    if (!push()) setTimeout(push, 800);
  };

  // Device-independent cart link for the mail's "Direkt bestellen" CTA. Liquid
  // only yields a token with key= sometimes; fetch it while the customer is
  // still typing their email, so the click path stays free of this request.
  const ensureCartUrl = (button) => {
    if (button.dataset.cartUrl) return;
    fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((c) => {
        const token = (c && c.token) || '';
        if (token.indexOf('key=') > -1) button.dataset.cartUrl = `https://kiyo.eu/cart/c/${token}`;
      })
      .catch(() => { /* CTA falls back to the template default */ });
  };

  const saveConfig = (button) => {
    const formId = button.dataset.formId;
    if (!formId || pendingSave) return;
    pendingSave = { button, formId };
    button.setAttribute('aria-busy', 'true');
    ensureCartUrl(button);
    try {
      // Prefill for logged-in customers: an identified profile makes Klaviyo
      // fill the email field itself.
      const known = button.dataset.customerEmail;
      if (known && window.klaviyo && typeof window.klaviyo.push === 'function') {
        window.klaviyo.push(['identify', { $email: known.toLowerCase() }]);
      }
      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push(['openForm', formId]);
    } catch (error) {
      console.warn('[Kiyo] Klaviyo-Formular liess sich nicht oeffnen:', error);
      button.removeAttribute('aria-busy');
      pendingSave = null;
    }
  };

  window.addEventListener('klaviyoForms', (event) => {
    const d = event.detail || {};
    if (!pendingSave || d.formId !== pendingSave.formId) return;
    const { button } = pendingSave;
    if (d.type === 'close') {
      button.removeAttribute('aria-busy');
      pendingSave = null;
      return;
    }
    if (d.type !== 'submit') return;
    button.removeAttribute('aria-busy');
    pendingSave = null;
    trackSaved(button, extractEmail(d.metaData), extractFirstName(d.metaData));
    feedback(button, button.dataset.savedLabel);
  });

  document.addEventListener('click', (event) => {
    const saveButton = event.target.closest('[data-kiyo-save]');
    if (saveButton) {
      saveConfig(saveButton);
      return;
    }
    const removeButton = event.target.closest('[data-kiyo-remove]');
    if (removeButton) {
      removeButton.disabled = true;
      removeGroup(removeButton.getAttribute('data-kiyo-remove'));
      return;
    }
    const shareButton = event.target.closest('[data-kiyo-share]');
    if (shareButton) {
      shareConfig(shareButton);
      return;
    }
    const unitsButton = event.target.closest('.kiyo-config-group__units-btn');
    if (unitsButton) {
      const wrap = unitsButton.closest('[data-kiyo-units]');
      const id = wrap.getAttribute('data-kiyo-units');
      const current = parseInt(wrap.querySelector('.kiyo-config-group__units-value')?.textContent, 10) || 1;
      const delta = parseInt(unitsButton.getAttribute('data-kiyo-units-delta'), 10) || 0;
      const next = Math.max(1, current + delta);
      if (next === current) return;
      wrap.querySelectorAll('.kiyo-config-group__units-btn').forEach((b) => { b.disabled = true; });
      setUnits(id, next);
    }
  });

  // Shopify's cart components morph rows back in after quantity updates on
  // other items — reveal our controls again whenever that happens.
  let scheduled = false;
  const observer = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      reveal();
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });

  // ── Mail link "Direkt bestellen" (Configurator Saved mail) ────────────────
  // ?kiyo_konfig=<base64url JSON { v, id, i: [[variantId, qty]], p: {_config_*} }>
  // built by kiyo-configurator (buildMailCartLink). Adds the saved configuration
  // to the EXISTING cart as one "Deine Konfiguration" group, then reloads the
  // cart without the parameter. Everything in the link is customer-editable,
  // so only known parts, sane quantities and whitelisted properties pass.
  // Component variants: keep in sync with VARIANT_MAP in kiyo-configurator.
  const CONFIG_VARIANTS = new Set([
    '44485135171892', '44485135204660', '50683815493962', '50683815559498', '50683815526730', '50683815461194', '60997711135050',
    '44485258871092', '44485258903860', '50683671806282', '50683315585354', '50683671839050', '50683671773514', '60997713363274',
    '61546326327626', '61546326360394', '61546326393162', '61546326425930', '61546326458698',
    '61546319282506', '61546319315274', '61546319348042', '61546319380810', '61546319413578',
  ]);
  const TEXT_PROPS = ['_config_dimensions', '_config_total', '_config_parts_text', '_config_colors'];
  const URL_PROPS = {
    _config_url: 'https://kiyo.eu/',
    _config_image: 'https://firebasestorage.googleapis.com/v0/b/kiyo-konfigurator.firebasestorage.app/o/screenshots',
  };

  const decodeMailLink = (raw) => {
    const binary = atob(raw.replace(/-/g, '+').replace(/_/g, '/'));
    const json = new TextDecoder().decode(Uint8Array.from(binary, (c) => c.charCodeAt(0)));
    const data = JSON.parse(json);
    if (!data || data.v !== 1 || typeof data.id !== 'string' || !/^[A-Za-z0-9-]{8,64}$/.test(data.id)) return null;
    if (!Array.isArray(data.i) || data.i.length === 0 || data.i.length > 40) return null;
    const items = [];
    for (const entry of data.i) {
      const [variantId, qty] = Array.isArray(entry) ? entry : [];
      if (!CONFIG_VARIANTS.has(String(variantId)) || !Number.isInteger(qty) || qty < 1 || qty > 200) return null;
      items.push({ id: Number(variantId), quantity: qty });
    }
    const p = (data.p && typeof data.p === 'object') ? data.p : {};
    const properties = { _config_id: data.id, _config_origin: 'mail' };
    TEXT_PROPS.forEach((key) => {
      if (typeof p[key] === 'string' && p[key].length <= 600) properties[key] = p[key];
    });
    Object.entries(URL_PROPS).forEach(([key, prefix]) => {
      if (typeof p[key] === 'string' && p[key].length <= 600 && p[key].startsWith(prefix)) properties[key] = p[key];
    });
    return { id: data.id, items, properties };
  };

  const addFromMailLink = async () => {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('kiyo_konfig');
    if (!raw) return;
    params.delete('kiyo_konfig');
    const query = params.toString();
    const cleanUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
    // Link scanners in mail gateways open links headless — never fill their carts.
    if (navigator.webdriver) {
      window.history.replaceState(null, '', cleanUrl);
      return;
    }
    const root = window.Shopify?.routes?.root || '/';
    const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
    try {
      const config = decodeMailLink(raw);
      if (config) {
        const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
        const alreadyIn = (cart.items || []).some((item) => item.properties && item.properties._config_id === config.id);
        if (!alreadyIn) {
          const response = await fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              items: config.items.map((item) => ({
                ...item,
                properties: { ...config.properties, _config_base_qty: String(item.quantity) },
              })),
            }),
          });
          if (!response.ok) throw new Error(`cart/add ${response.status}`);
          const list = String((cart.attributes && cart.attributes.Konfigurationen) || '')
            .split(',').map((s) => s.trim()).filter(Boolean);
          if (!list.includes(config.id)) list.push(config.id);
          await fetch(`${root}cart/update.js`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ attributes: { Source: 'configurator', Konfigurationen: list.join(',') } }),
          });
        }
      }
    } catch (error) {
      console.warn('[Kiyo] Konfiguration aus dem Mail-Link nicht eingelegt:', error);
    }
    // Server-render the cart with the group (or unchanged if the link was invalid).
    window.location.replace(cleanUrl);
  };

  addFromMailLink();
  reveal();
})();
